import * as React from "react";
import { AlertMessage, AlertType, ButtonType, Checkbox, CustomButton, CustomDatePicker, CustomSubmitButton, DatePickerRange, Dropdown, FileUpload, Form, HiddenInput, HsaIdInput, Modal, ModalBody, ModalFooter, ModalSize, MultiSelect, PersonalIdentityInput, Select, TextInput, Textarea } from "soprasteria-ui-library";
import { useAuth } from "../../../../../App";
import { ICarePerformerRequest, ICarePerformerRequestUploadedFile } from "../../../../../models/ICarePerformerRequest";
import { IListItem } from "soprasteria-ui-library/dist/models/IFormInput";
import { CarePerformerRequestApi } from "../../../../../api/CarePerformerRequestApi"
import { CareAreaApi } from "../../../../../api/CareAreaApi"
import { UserApi } from "../../../../../api/UserApi"
import { ClinicApi } from "../../../../../api/ClinicApi"
import { SearchUser } from "../../../../Search/SearchUser";
import { IUser } from "../../../../../models/IUser";
import { IClinic } from "../../../../../models/IClinic";
import { TANDHYGIENIST, TANDLÄKARE } from "../../../../../utils/CareLicenseTypeConstants";
import { SPECIALISTTANDVÅRD } from "../../../../../utils/CareTypeConstants";
import { setMessage } from "../../../../../helpers/MessageHelper";
import { formatDate, getStartAndEndDateDisplayValue } from "../../../../../helpers/DateHelper";
import { formatPersonalSecurityNumber } from "../../../../../helpers/PersonalSecurityNumberAndOrganisationNumberHelper";
import { booleanToString } from "../../../../../helpers/BooleanHelper";
import { UserCompetenceApi } from "../../../../../api/UserCompetenceApi";
import { nameOf } from "../../../../../helpers/PropertyNameHelper";
import { megaBytesToBytes } from "../../../../../helpers/FileHelper";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faDownload } from "@fortawesome/free-solid-svg-icons";
import "./CarePerformerRequestModal.scss";
import { emailAddressRegExp } from "../../../../../helpers/RegExpHelper";

interface IProps {
    openedFromClinicId: number;
    carePerformerRequest: ICarePerformerRequest | undefined;
    onSubmit: (clinicId: number) => void;
    closeModal: () => void;
}

export const CarePerformerRequestModal: React.FunctionComponent<IProps> = ({ openedFromClinicId, carePerformerRequest, onSubmit, closeModal }) => {
    const auth = useAuth();
    const api = new CarePerformerRequestApi(auth);
    const userApi = new UserApi(auth);
    const clinicApi = new ClinicApi(auth);
    const careAreaApi = new CareAreaApi(auth);
    const userCompetenceApi = new UserCompetenceApi(auth);
    const abortController = new AbortController();
    const [currentUser, setCurrentUser] = React.useState<IUser>();
    const [selectedUser, setSelectedUser] = React.useState<IUser>();
    const [errorMessage, setErrorMessage] = React.useState<string>("");
    const [isLoading, setIsLoading] = React.useState<boolean>(false);
    const [dateToday] = React.useState<Date>(new Date());
    const [clinic, setClinic] = React.useState<IClinic>();
    const [careLicenseTypes, setCareLicenseTypes] = React.useState<IListItem[]>();
    const [careAssignments, setCareAssignments] = React.useState<IListItem[]>();
    const [dentalHealthQualifications, setDentalHealtQualifications] = React.useState<IListItem[]>();
    const [selectedDentalHealthQualificationIds, setSelectedDentalHealthQualificationIds] = React.useState<string | string[]>();
    const [isDentistSelected, setIsDentistSelected] = React.useState<boolean>(false);
    const [isDentalHygienistSelected, setIsDentalHygienistSelected] = React.useState<boolean>(false);
    const [isSDentalCareSelected, setIsSDentalCareSelected] = React.useState<boolean>(false);
    const [hasSpecialistCompetence, setHasSpecialistCompetence] = React.useState<boolean>(false);
    const [isInSpecialistTraining, setIsInSpecialistTraining] = React.useState<boolean>(false);
    const [isNewUserSelected, setIsNewUserSelected] = React.useState<boolean>(false);

    React.useEffect(() => {
        document.getElementById("open-care-performer-request-modal")?.click();

        loadClinic();
        loadCareLicenseTypes();
        loadCareAssignments();
        loadCurrentUser();

        if (carePerformerRequest) {
            loadSelectedUser(carePerformerRequest.userId);
            if (carePerformerRequest.careAssignmentId) {
                setHasSpecialistCompetence(true);
            }
            if (carePerformerRequest.careLicenseTypeId.toString() === TANDLÄKARE) {
                setIsDentistSelected(true);
            }
            if (carePerformerRequest.careLicenseTypeId.toString() === TANDHYGIENIST) {
                setIsDentalHygienistSelected(true);
            }
            if (carePerformerRequest.isInSpecialistTraining) {
                setIsInSpecialistTraining(true);
            }
        }

        return () => {
            abortController.abort();
        }
    }, [carePerformerRequest]);

    const loadClinic = () => {
        setIsLoading(true);
        clinicApi.getClinicByClinicId(carePerformerRequest ? carePerformerRequest.clinicId : openedFromClinicId, (result) => {
            setClinic(result);
        }, (error) => { setErrorMessage(error) }, () => { setIsLoading(false); });
    }

    const loadCareLicenseTypes = () => {
        setIsLoading(true);
        userCompetenceApi.getCareLicenseTypes((data) => {
            const mappedCareLicenseTypes = data.map<IListItem>((careLicenseType) => { return { text: careLicenseType.name, value: careLicenseType.careLicenseTypeId.toString() } });
            setCareLicenseTypes(mappedCareLicenseTypes)
        }, undefined, () => setIsLoading(false));
    }

    const loadCareAssignments = () => {
        setIsLoading(true);
        careAreaApi.getCareAssignmentsByCareTypeId(SPECIALISTTANDVÅRD, (data) => {
            const mappedCareAssignments = data.map<IListItem>((careAssignment) => { return { text: careAssignment.specialistFunctionName, value: careAssignment.careAssignmentId.toString() } });
            setCareAssignments(mappedCareAssignments);
        }, undefined, () => setIsLoading(false));
    }

    const loadDentalHealthQualifications = (careLicenseTypeId: number) => {
        setIsLoading(true);
        userCompetenceApi.getDentalHealthQualificationsByCareLicenseTypeId(careLicenseTypeId, (data) => {
            const mappedDentalHealthQualifications = data.map<IListItem>((dhq) => { return { text: `${dhq.name} - ${dhq.description}`, value: dhq.dentalHealthQualificationId.toString() } });
            setDentalHealtQualifications(mappedDentalHealthQualifications);
        }, undefined, () => setIsLoading(false));
    }

    const loadCurrentUser = () => {
        setIsLoading(true);
        userApi.getCurrentUser((data) => {
            setCurrentUser(data);
        }, undefined, () => setIsLoading(false));
    }

    const loadSelectedUser = (userId?: number) => {
        if (userId) {
            setIsLoading(true);
            userApi.getUserByUserId(userId, (data) => {
                setSelectedUser(data);
            }, undefined, () => setIsLoading(false));
        }
    }

    const onCareLicenseTypeChange = (selectedValue?: string) => {
        setIsSDentalCareSelected(false);
        setSelectedDentalHealthQualificationIds(undefined);
        if (selectedValue === TANDLÄKARE) {
            setIsDentistSelected(true);
            setIsDentalHygienistSelected(false);
        } else if (selectedValue === TANDHYGIENIST) {
            setIsDentistSelected(false);
            setIsDentalHygienistSelected(true);
            setHasSpecialistCompetence(false);
        }
        if (selectedValue) {
            loadDentalHealthQualifications(parseInt(selectedValue));
        }
    }

    const onHasSpecialistCompetenceChange = (checked: boolean) => {
        setHasSpecialistCompetence(checked);
        if (!checked) {
            setIsInSpecialistTraining(false);
        }
    }

    const onIsSDentalCareChange = (checked: boolean) => {
        setIsSDentalCareSelected(checked);
    }

    const onDentalHealthQualificationChange = (selectedValues?: string | string[]) => {
        setSelectedDentalHealthQualificationIds(selectedValues);
    }

    const onIsInSpecialistTrainingChange = (checked: boolean) => {
        setIsInSpecialistTraining(checked);
    }

    const onNewUserChange = (checked: boolean) => {
        setIsNewUserSelected(checked);
    }

    const onFormSubmit = (data: ICarePerformerRequest) => {
        setIsLoading(true);
        if (data.carePerformerRequestId.toString() === "0") {
            api.createCarePerformerRequest(data, (createdCarePerformerRequestId) => {
                onSubmit(createdCarePerformerRequestId);
            }, (message: string) => {
                const newMessage = setMessage(message, errorMessage);
                setErrorMessage(newMessage);
            }, () => setIsLoading(false));
        } else {
            console.log("not implemented yet");
        }
    }

    const selectedUserFullName = selectedUser ? `${selectedUser.hsaId} - ${selectedUser.firstName} ${selectedUser.lastName}` : '';

    const documentsLabel = () => {
        if (selectedDentalHealthQualificationIds && selectedDentalHealthQualificationIds.length > 1) {
            return `Ladda upp ${selectedDentalHealthQualificationIds.length} filer`;
        } else if (selectedDentalHealthQualificationIds && selectedDentalHealthQualificationIds.length == 1) {
            return `Ladda upp 1 fil`;
        } else {
            return ""
        }
    }

    const renderUserField = () => {
        if (selectedUser && carePerformerRequest) {
            return (
                <TextInput label="Användare" name="readonly" defaultValue={selectedUserFullName} readonly inlineLabel />
            );
        } else if (selectedUser && carePerformerRequest === undefined) {
            return (
                <>
                    <CustomButton buttonText="Sök upp ny användare" buttonType={ButtonType.edit} onClick={() => setSelectedUser(undefined)} />
                    <TextInput label="Användare" name="readonly" defaultValue={selectedUserFullName} readonly inlineLabel />
                </>
            );
        } else if (selectedUser === undefined) {
            return (
                <>
                    {
                        !isNewUserSelected ?
                            <SearchUser name="searchUser" label="Användare" placeholder="Sök användare..." onChange={(val: string) => loadSelectedUser(parseInt((val)))} errorMessage="Användare måste anges" required />
                            : null
                    }
                    <Checkbox name="addNew" label="Ny användare" value="true" onChange={onNewUserChange} />
                    {
                        isNewUserSelected ?
                            <>
                                <TextInput label="Förnamn" name="newUser.userFirstName" required inlineLabel />
                                <TextInput label="Efternamn" name="newUser.userLastName" required inlineLabel />
                                <TextInput label="Telefonnummer" name="newUser.userPhoneNumber" inlineLabel />
                                <TextInput label="Mobiltelefonnummer" name="newUser.userMobilePhoneNumber" inlineLabel />
                                <HsaIdInput label="HSA-ID" name="newUser.userHsaId" required inlineLabel />
                                <TextInput label="E-postadress" name="newUser.userEmailAddress" maxLength={50} pattern={emailAddressRegExp()} patternValidationMessage="Ogiltigt format på e-postadress" required inlineLabel />
                            </> : null
                    }

                </>
            );
        }
        return null;
    }

    const renderSDentalHealthSection = () => {
        if (carePerformerRequest !== undefined && (carePerformerRequest.hasS9Qualification || carePerformerRequest.hasS10Qualification || carePerformerRequest.uploadedFiles.length > 0)) {
            return (
                <>
                    {
                        carePerformerRequest.hasS9Qualification &&
                        <TextInput label="Har kompetens inom S9" name="readonly" defaultValue={booleanToString(carePerformerRequest.hasS9Qualification, "-")} inlineLabel readonly />
                    }
                    {
                        carePerformerRequest.hasS10Qualification &&
                        <TextInput label="Har kompetens inom S10" name="readonly" defaultValue={booleanToString(carePerformerRequest.hasS10Qualification, "-")} inlineLabel readonly />
                    }
                    {
                        carePerformerRequest.uploadedFiles.length > 0 ?
                            <>
                                <div className="col-12 files">
                                    <div className="row">
                                        <div className="col-12">
                                            <hr />
                                            <h5>Uppladdade bevis</h5>
                                            <div className="row">
                                                <h6 className="col-10">Filnamn</h6>
                                                <h6 className="col-2">Ladda ner</h6>
                                            </div>
                                        </div>
                                    </div>
                                    <ul className="list-no-styling document-download-list">
                                        {
                                            carePerformerRequest.uploadedFiles.map((uploadedFile, index) =>
                                                renderUploadedFileRow(carePerformerRequest.carePerformerRequestId, uploadedFile, index)
                                            )
                                        }
                                    </ul>
                                    <hr />
                                </div>
                            </> : null
                    }
                </>)
        }
    }

    const renderUploadedFileRow = (carePerformerRequestId: number, uploadedFile: ICarePerformerRequestUploadedFile, key: number) => {
        return <li key={key} className="row" onClick={() => downloadDocument(carePerformerRequestId, uploadedFile.carePerformerRequestUploadedFileId, uploadedFile.fileName)} title={`Ladda ner ${uploadedFile.fileName}`}>
            <div className="col-12 download">
                <div className="row">
                    <p className="col-10">{uploadedFile.fileName}</p>
                    <span className="col-2"><FontAwesomeIcon icon={faDownload} /></span>
                </div>
            </div>
        </li>
    }

    const downloadDocument = (carePerformerRequestId: number, carePerformerRequestUploadedFileId: number, fileName?: string) => {
        api.downloadCarePerformerRequestUploadedFile(carePerformerRequestId, carePerformerRequestUploadedFileId, fileName,
            (error) => {
                alert(error);
            });
    }

    const careAssignmentDateLabel = isInSpecialistTraining ? "Datum för på påbörjad specialisttjänstgöring" : "Datum för bevis om specialistkompetens";

    const confirmText = currentUser && selectedUser && carePerformerRequest === undefined ? `Jag, ${currentUser.firstName} ${currentUser.lastName}, försäkrar på heder och samvete att ${selectedUserFullName} innehar erforderliga legitimationer och bevis om eventuell specialistkompetens` : '';
    const confirmNewUserText = currentUser && selectedUser === undefined && isNewUserSelected ? `Jag, ${currentUser.firstName} ${currentUser.lastName}, försäkrar på heder och samvete att angiven användare innehar erforderliga legitimationer och bevis om eventuell specialistkompetens` : '';

    return (
        <Modal id="care-performer-request-modal" modalSize={ModalSize.large} header={carePerformerRequest ? "Visa ansökan" : "Skapa ansökan"}>
            <Form onSubmit={onFormSubmit}>
                <ModalBody>
                    <AlertMessage alertType={AlertType.danger} duration={5000} >{errorMessage}</AlertMessage>
                    <div className="row">
                        <div className="col-12">
                            <HiddenInput name={nameOf<ICarePerformerRequest>('carePerformerRequestId')} value={carePerformerRequest?.carePerformerRequestId ? carePerformerRequest.carePerformerRequestId.toString() : "0"} />
                            <HiddenInput name={nameOf<ICarePerformerRequest>('clinicId')} value={openedFromClinicId.toString()} />
                            <HiddenInput name={nameOf<ICarePerformerRequest>('userId')} value={selectedUser ? selectedUser.userId.toString() : "0"} />

                            {
                                carePerformerRequest ?
                                    <>
                                        <TextInput label="Skapad av" name="readonly" defaultValue={carePerformerRequest.regByFullName} readonly inlineLabel />
                                        <TextInput label="Skapad datum" name="readonly" defaultValue={formatDate(carePerformerRequest.regDate)} readonly inlineLabel />
                                        <TextInput label="Status" name="readonly" defaultValue={carePerformerRequest.statusName} readonly inlineLabel />
                                        {
                                            carePerformerRequest.decisionDate ?
                                                <>
                                                    <TextInput label="Beslutsdatum" name="readonly" defaultValue={formatDate(carePerformerRequest.decisionDate)} readonly inlineLabel />
                                                    <TextInput label="Beslut" name="readonly" defaultValue={carePerformerRequest.decisionTypeName} readonly inlineLabel />
                                                    {
                                                        carePerformerRequest.decisionMotivation ?
                                                            <Textarea label="Motivering" name="readonly" defaultValue={carePerformerRequest.decisionMotivation} readonly inlineLabel />
                                                            : null
                                                    }

                                                </>
                                                : null
                                        }
                                        <hr />
                                    </> : null
                            }
                            {renderUserField()}
                            {
                                carePerformerRequest === undefined ?
                                    <PersonalIdentityInput label="Personnummer" name={nameOf<ICarePerformerRequest>('personalIdentityNumber')} placeholder="ÅÅÅÅMMDD-NNNN" tooltipDescription="Personnummer eller samordningsnummer" inlineLabel required />
                                    : <TextInput label="Personnummer" name="readonly" defaultValue={formatPersonalSecurityNumber(carePerformerRequest.personalIdentityNumber)} readonly inlineLabel />
                            }
                            {
                                carePerformerRequest === undefined ?
                                    <DatePickerRange label="Anställningsdatum fr.o.m. - t.o.m."
                                        name={nameOf<ICarePerformerRequest>('dateFrom')}
                                        value={dateToday}
                                        valueSecondary={undefined}
                                        nameSecondary={nameOf<ICarePerformerRequest>('dateTo')}
                                        tooltipDescription="Om anställningen är tillsvidare kan fältet t.o.m. lämnas tomt"
                                        requiredFrom
                                        inlineLabel />
                                    : <TextInput label="Anställningsdatum fr.o.m. - t.o.m." name="readonly" defaultValue={getStartAndEndDateDisplayValue(carePerformerRequest.dateFrom, carePerformerRequest.dateTo ? carePerformerRequest.dateTo : undefined)} readonly inlineLabel />
                            }
                            {
                                carePerformerRequest === undefined ?
                                    <Select label="Legitimation" name={nameOf<ICarePerformerRequest>('careLicenseTypeId')} placeholder="Välj en legitimation" options={careLicenseTypes ?? []} tooltipDescription="Legitimationsbevis från Socialstyrelsen för tandläkare eller tandhygienist" onChange={onCareLicenseTypeChange} required inlineLabel />
                                    : <TextInput label="Legitimation" name="readonly" defaultValue={careLicenseTypes && careLicenseTypes.find(c => c.value === carePerformerRequest.careLicenseTypeId.toString())?.text} readonly inlineLabel />
                            }
                            {
                                carePerformerRequest === undefined ?
                                    <CustomDatePicker label="Legitimationsdatum" name={nameOf<ICarePerformerRequest>('licenseDate')} required inlineLabel />
                                    : <TextInput label="Legitimationsdatum" name="readonly" defaultValue={formatDate(carePerformerRequest.licenseDate)} readonly inlineLabel />
                            }
                            {
                                (isDentistSelected || isDentalHygienistSelected) && carePerformerRequest === undefined ?
                                    <Checkbox label="S-tandvård:" name="sDentalCare" id="sDentalCare" value="true" checked={isSDentalCareSelected} onChange={onIsSDentalCareChange} />
                                    : null
                            }
                            {
                                isSDentalCareSelected && dentalHealthQualifications && carePerformerRequest === undefined ?
                                    <MultiSelect label="Kompetens" name={nameOf<ICarePerformerRequest>('dentalHealthQualificationIds')} values={dentalHealthQualifications} onValueChange={onDentalHealthQualificationChange} placeholder={isDentistSelected ? "Välj en eller flera kompetenser" : "Välj en kompetens"} isMultiple required />
                                    : null
                            }
                            {
                                isSDentalCareSelected && selectedDentalHealthQualificationIds && selectedDentalHealthQualificationIds.length > 0 ?
                                    <>
                                        <FileUpload name="attachments"
                                            label={documentsLabel()}
                                            maxSizeBytes={megaBytesToBytes(5)}
                                            allowedFileTypes={[".pdf"]}
                                            multiple={selectedDentalHealthQualificationIds.length > 1}
                                            maxFiles={selectedDentalHealthQualificationIds.length}
                                            required
                                            inlineLabel
                                        />
                                    </> : null
                            }
                            {
                                renderSDentalHealthSection()
                            }
                            {
                                isDentistSelected ?
                                    <>
                                        {
                                            carePerformerRequest === undefined ?
                                                <Checkbox label="Har specialistkompetens:" name="hasSpecialistCompetence" id="hasSpecialistCompetence" value="true" onChange={onHasSpecialistCompetenceChange} />
                                                : null
                                        }

                                        {
                                            hasSpecialistCompetence ?
                                                <>
                                                    {
                                                        carePerformerRequest === undefined ?
                                                            <Checkbox label="Under specialisttjänstgöring:" name={nameOf<ICarePerformerRequest>('isInSpecialistTraining')} id={nameOf<ICarePerformerRequest>('isInSpecialistTraining')} value="true" onChange={onIsInSpecialistTrainingChange} />
                                                            : <TextInput label="Under specialisttjänstgöring" name="readonly" defaultValue={booleanToString(carePerformerRequest.isInSpecialistTraining)} readonly inlineLabel />
                                                    }
                                                    {
                                                        carePerformerRequest === undefined ?
                                                            <Select label="Bevis om specialistkompetens" name={nameOf<ICarePerformerRequest>('careAssignmentId')} placeholder="Välj en specialistkompetens" options={careAssignments ?? []} tooltipDescription="Bevis om specialistkompetens efter slutförd specialisttjänstgöring. Om behandlaren är under specialisttjänstgöring bockas under specialisttjänstgöring i" required inlineLabel />
                                                            : <TextInput label="Bevis om specialistkompetens" name="readonly" defaultValue={carePerformerRequest.careAssignmentId && careAssignments ? careAssignments.find(c => c.value === carePerformerRequest.careAssignmentId!.toString())?.text : undefined} readonly inlineLabel />
                                                    }
                                                    {
                                                        carePerformerRequest === undefined ?
                                                            <CustomDatePicker label={careAssignmentDateLabel} name={nameOf<ICarePerformerRequest>('careAssignmentDate')} required inlineLabel />
                                                            : <TextInput label={careAssignmentDateLabel} name="readonly" defaultValue={formatDate(carePerformerRequest.careAssignmentDate)} readonly inlineLabel />
                                                    }
                                                </>
                                                : null
                                        }
                                    </>
                                    : null
                            }
                        </div>
                        {
                            (confirmText && confirmText !== "") || (confirmNewUserText && confirmNewUserText) !== "" ?
                                <>
                                    <div className="col-12"><hr /></div>
                                    <div className="col-12">
                                        <Checkbox value="agreement" label={confirmText !== "" ? confirmText : confirmNewUserText} name="agreement" id="agreement" required labelCol={7} inputCol={5} />
                                    </div>
                                </>
                                : null
                        }
                    </div>
                </ModalBody>
                <ModalFooter>
                    <div className="text-right btn-group" role="group">
                        <CustomButton buttonType={ButtonType.general} id="close-care-performer-request-modal" data-dismiss="modal" onClick={() => closeModal()} buttonText={carePerformerRequest ? "Stäng" : "Avbryt"} />
                        <CustomSubmitButton isLoading={isLoading} hidden={carePerformerRequest !== undefined}>Ansök</CustomSubmitButton>
                        <input type="hidden" data-toggle="modal" id="open-care-performer-request-modal" data-target="#care-performer-request-modal" />
                    </div>
                </ModalFooter>
            </Form>
        </Modal>
    );
}